import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Check, Calendar, Download } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { downloadICS, googleCalendarLink } from './calendarExport';

const STATUS_CONFIG = {
  present:  { label: 'Presente',    cls: 'border-green-500 text-green-400 bg-green-500/10' },
  absent:   { label: 'Faltou',      cls: 'border-red-500 text-red-400 bg-red-500/10' },
  excused:  { label: 'Justificado', cls: 'border-yellow-500 text-yellow-400 bg-yellow-500/10' },
};

export default function AttendanceModal({
  session, date,
  sessionTypes, agendaStudents,
  token, backendUrl,
  onSaved, onClose,
}) {
  const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
  const sessionType = sessionTypes.find(t => t.id === session.typeId);
  const sessionStudents = agendaStudents.filter(s => session.studentIds.includes(s.id));

  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        const res = await axios.get(
          `${backendUrl}/api/admin/agenda/attendance?sessionId=${session.id}&date=${dateStr}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const map = {};
        for (const r of res.data) map[r.agendaStudentId] = r.status;
        setStatuses(map);
      } catch {}
      setLoading(false);
    };
    fetchAttendance();
  }, [session.id, dateStr, backendUrl, token]);

  const handleSave = async () => {
    setSaving(true);
    const records = sessionStudents.map(s => ({
      sessionId: session.id,
      agendaStudentId: s.id,
      date: dateStr,
      status: statuses[s.id] || 'absent',
    }));
    try {
      await axios.post(`${backendUrl}/api/admin/agenda/attendance`,
        { records },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaved(true);
      setTimeout(() => onSaved?.(), 800);
    } catch { setSaving(false); }
  };

  const dateLabel = (() => {
    try { return format(parseISO(dateStr), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR }); }
    catch { return dateStr; }
  })();

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-white/10 w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-widest">
              {sessionType?.name || 'Sessão'}
              {session.title && <span className="text-[#A3A3A3] font-normal ml-2">— {session.title}</span>}
            </h2>
            <p className="text-xs text-[#555] mt-0.5 capitalize">{dateLabel} • {session.time}</p>
          </div>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X size={16} /></button>
        </div>

        <div className="p-6">
          {loading ? (
            <p className="text-[#555] text-sm text-center py-4">Carregando...</p>
          ) : sessionStudents.length === 0 ? (
            <p className="text-[#555] text-sm text-center py-4">Nenhum aluno nesta sessão.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {sessionStudents.map(student => {
                const current = statuses[student.id];
                return (
                  <div key={student.id}
                    className={`border p-4 transition-colors ${current ? STATUS_CONFIG[current].cls : 'border-white/10'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{student.name}</span>
                      <div className="flex gap-1.5">
                        {Object.entries(STATUS_CONFIG).map(([status, cfg]) => (
                          <button key={status}
                            onClick={() => setStatuses(s => ({ ...s, [student.id]: status }))}
                            className={`px-2.5 py-1 text-[11px] border transition-colors ${
                              current === status
                                ? cfg.cls
                                : 'border-white/10 text-[#555] hover:border-white/20 hover:text-[#A3A3A3]'
                            }`}
                          >
                            {cfg.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 pb-2">
          <button onClick={() => setShowExport(!showExport)}
            className="flex items-center gap-2 text-xs text-[#555] hover:text-[#A3A3A3] transition-colors"
          >
            <Calendar size={12} /> Adicionar ao calendário
          </button>
          {showExport && (
            <div className="flex gap-2 mt-2 pl-4">
              <button
                onClick={() => downloadICS(session, sessionType, sessionStudents, dateStr)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-white/10 hover:border-white/30 text-xs text-[#A3A3A3] hover:text-white transition-colors"
              >
                <Download size={11} /> Baixar .ics
              </button>
              <a
                href={googleCalendarLink(session, sessionType, sessionStudents, dateStr)}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 border border-white/10 hover:border-white/30 text-xs text-[#A3A3A3] hover:text-white transition-colors"
              >
                Google Calendar
              </a>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-white/10">
          {confirmDelete ? (
            <div>
              <p className="text-xs text-[#A3A3A3] mb-3">
                Excluir esta sessão?{' '}
                <span className="text-[#555]">As presenças associadas também serão removidas.</span>
              </p>
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      await axios.delete(`${backendUrl}/api/admin/agenda/sessions/${session.id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      onSaved?.();
                    } catch {}
                  }}
                  className="px-4 py-2 bg-red-500/80 hover:bg-red-500 text-white text-sm font-bold uppercase tracking-wider transition-colors"
                >
                  Excluir
                </button>
                <button onClick={() => setConfirmDelete(false)}
                  className="px-4 py-2 border border-white/10 hover:border-white/30 text-sm text-[#A3A3A3] transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <button onClick={() => setConfirmDelete(true)}
                className="text-[#555] hover:text-red-400 text-xs transition-colors"
              >
                Excluir sessão
              </button>
              <div className="flex gap-3">
                <button onClick={onClose}
                  className="px-4 py-2 border border-white/10 hover:border-white/30 text-sm text-[#A3A3A3] transition-colors"
                >
                  Cancelar
                </button>
                <button onClick={handleSave} disabled={saving || saved || sessionStudents.length === 0}
                  className="flex items-center gap-2 px-5 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50"
                >
                  {saved ? <><Check size={14} /> Salvo</> : saving ? 'Salvando...' : 'Salvar presenças'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
